import React, { useState } from 'react';
import { IonIcon, useIonToast } from '@ionic/react';
import { shieldCheckmark } from 'ionicons/icons';
import {
  useMemberConsents,
  useRecordConsent,
  useWithdrawConsent,
} from '@/hooks/useConsents';
import ConfirmDialog from '@/components/shared/ConfirmDialog';

interface Props {
  memberId: string;
  isAdmin: boolean;
}

const MemberConsentCard: React.FC<Props> = ({ memberId, isAdmin }) => {
  const [present] = useIonToast();
  const { data: consents, isLoading } = useMemberConsents(memberId);
  const recordMut = useRecordConsent(memberId);
  const withdrawMut = useWithdrawConsent(memberId);
  const [showWithdrawConfirm, setShowWithdrawConfirm] = useState(false);

  const activeConsents = consents?.filter((c) => c.is_active) ?? [];
  const hasGdpr = activeConsents.length > 0;
  const isBusy = recordMut.isPending || withdrawMut.isPending;

  const handleToggle = async () => {
    if (isBusy) return;
    if (hasGdpr) {
      setShowWithdrawConfirm(true);
      return;
    }
    try {
      await recordMut.mutateAsync({
        type: 'data_processing',
        method: 'physical_signature',
        consented_at: new Date().toISOString().slice(0, 10),
      });
      present({ message: 'GDPR semnat.', duration: 2000, color: 'success', position: 'bottom' });
    } catch {
      present({ message: 'Eroare la actualizare GDPR.', duration: 3000, color: 'danger', position: 'bottom' });
    }
  };

  const handleWithdraw = async () => {
    try {
      for (const c of activeConsents) {
        await withdrawMut.mutateAsync(c.id);
      }
      setShowWithdrawConfirm(false);
      present({ message: 'GDPR retras.', duration: 2000, color: 'success', position: 'bottom' });
    } catch {
      present({ message: 'Eroare la retragerea GDPR.', duration: 3000, color: 'danger', position: 'bottom' });
    }
  };

  return (
    <div className="detail-card">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h3 className="detail-card__title" style={{ margin: 0 }}>
          <IonIcon icon={shieldCheckmark} style={{ fontSize: 16, marginRight: 6, verticalAlign: -2, color: '#5e81f4' }} />
          Consimțământ GDPR
        </h3>
        {isAdmin && (
          <button
            onClick={handleToggle}
            disabled={isBusy || isLoading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 10,
              padding: '6px 14px 6px 8px',
              borderRadius: 999,
              border: `1px solid ${hasGdpr ? 'rgba(5,150,105,0.2)' : 'rgba(180,83,9,0.2)'}`,
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '0.02em',
              cursor: isBusy ? 'wait' : 'pointer',
              transition: 'all 0.2s',
              background: hasGdpr ? '#ecfdf5' : '#fffbeb',
              color: hasGdpr ? '#047857' : '#b45309',
              boxShadow: hasGdpr
                ? '0 1px 2px rgba(5,150,105,0.08)'
                : '0 1px 2px rgba(180,83,9,0.08)',
            }}
          >
            <span
              style={{
                width: 30,
                height: 18,
                borderRadius: 999,
                background: hasGdpr ? '#10b981' : '#cbd5e1',
                position: 'relative',
                transition: 'background 0.2s',
                flexShrink: 0,
                boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.08)',
              }}
            >
              <span
                style={{
                  position: 'absolute',
                  top: 2,
                  left: hasGdpr ? 14 : 2,
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  background: '#fff',
                  transition: 'left 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.18), 0 0 0 0.5px rgba(0,0,0,0.04)',
                }}
              />
            </span>
            {isBusy ? '...' : hasGdpr ? 'Semnat' : 'Nesemnat'}
          </button>
        )}
        {!isAdmin && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 10px 3px 8px',
              borderRadius: 999,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
              background: hasGdpr ? '#ecfdf5' : '#fffbeb',
              color: hasGdpr ? '#047857' : '#b45309',
              border: `1px solid ${hasGdpr ? 'rgba(5,150,105,0.2)' : 'rgba(180,83,9,0.2)'}`,
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: hasGdpr ? '#10b981' : '#d97706',
                boxShadow: `0 0 0 2px ${hasGdpr ? 'rgba(16,185,129,0.15)' : 'rgba(217,119,6,0.15)'}`,
              }}
            />
            {isLoading ? '...' : hasGdpr ? 'Semnat' : 'Nesemnat'}
          </span>
        )}
      </div>

      <ConfirmDialog
        isOpen={showWithdrawConfirm}
        title="Retragere GDPR"
        message="Ești sigur că vrei să retragi consimțământul GDPR?"
        confirmLabel="Retrage"
        variant="warning"
        isLoading={withdrawMut.isPending}
        onConfirm={handleWithdraw}
        onCancel={() => setShowWithdrawConfirm(false)}
      />
    </div>
  );
};

export default MemberConsentCard;
